"use strict";

const { isNullOrEmpty, isTrue }  = require("@nuskin/ns-common-lib");

const Product = require("./product.js");
const ProductUtils = require("./productUtils.js");

/**
 * Contains functions to map the AEM Product Content to a Product (product.js).
 */
const ProductContentMapper = {
  /**
   * Creates a Product (product.js) object from the AEM Product Content response.
   * @param {Object} productContent the AEM response
   * @param {Object} options {language, country, isGroupOffer, isPersonalOffer, productStatus, priceType}
   */
  createProduct: function(productContent, options = {}) {
    if (isNullOrEmpty(productContent)) {
      return null;
    }

    if (isNullOrEmpty(options)) {
      options = {};
    }

    let product = new Product();

    // set market values
    let marketData = productContent.market;

    if (Array.isArray(marketData)) {
      marketData = getMarketData(marketData, options.country);
    }

    if (marketData) {
      product.country = marketData.countryCode;
      product.productLabels = marketData.populateProductLabels;
      product.scanQualified = marketData.scanQualified;

      const marketOrderTypes = marketData.orderType;
      if (marketOrderTypes) {
        product.orderTypes = ProductUtils.getOrderTypesMapping(marketOrderTypes);
      }

      const marketCustomerTypes = marketData.custType;
      if (marketCustomerTypes) {
        product.custTypes = marketCustomerTypes;
      }

      const restrictedMarkets = marketData.restrictedMarket;
      if (restrictedMarkets) {
        product.restrictedMarkets = restrictedMarkets;
      }

      if (marketData.addOns) {
        product.addOns = marketData.addOns;
      }
    }

    // set product values
    product.sku = productContent.sku;
    product.isExclusive = isTrue(productContent.isExclusive);

    if (productContent.contents) {
      const fullImage = productContent.contents.fullImage;
      if (fullImage) {
        product.setFullImage(fullImage);
        product.setThumbnail(
          fullImage.replace(
            /\.img\.[0-9]+\.[0-9]+\.png/i,
            ".img.160.160.png"
          )
        );
      }

      const imageCarousel = productContent.contents.imageCarousel;
      if (imageCarousel) {
        product.setProductCarouselImages(imageCarousel);
      }

      // set product language data
      let languageData = getLanguageData(
        productContent.contents.language,
        options.language
      );
  
      // AEM-8320 - it returns two BP (08007070 and 08129080), 08129080 does not have any translation yet.
      // language data can be null (have a BP that does not have any translation)
      if (languageData) {
        product.lang = languageData.languageCode || "";
        product.title = languageData.name || "";
        product.shortDescr = languageData.shortDescription || "";
        product.longDescr = languageData.longDescription || "";
        product.ingredients = languageData.ingredients || "";
        product.benefits = languageData.benefits || "";
        product.usage = languageData.usage || "";
        product.resources = languageData.resources || "";
        product.videos = languageData.videos || "";
        product.contentSection = languageData.contentSection || "";
        product.sizeWeight = languageData.size_weight || "";
        product.nettoWeight = languageData.netto_weight || "";
        product.movie = languageData.movie || "";
        product.youtube = languageData.youtube || "";
        product.salesEventText = languageData.tdc_salesEventText || "";
        product.salesLabel = languageData.salesLabel || "";

        if (languageData.variantInfo) {
          if (languageData.variantInfo.tdc_dropdownLabel) {
            product.variantDropdownLabel =
            languageData.variantInfo.tdc_dropdownLabel;
          }
          if (languageData.variantInfo.tdc_dropdownPlaceholder) {
            product.variantDropdownPlaceholder =
            languageData.variantInfo.tdc_dropdownPlaceholder;
          }
          if (languageData.variantInfo.tdc_variantsLabel) {
            product.variantsLabel = languageData.variantInfo.tdc_variantsLabel;
          }
        }
  
        product.setImageAltText(productContent.contents.altText);
      }
    }

    product.division = productContent.division;
  
    product.isGroupOffer = !!options.isGroupOffer;
    product.isPersonalOffer = !!options.isPersonalOffer;

    // set product variants values
    if (productContent.variantConfig) {
      if (productContent.variantConfig.variantType) {
        product.variantType = productContent.variantConfig.variantType;
      }
      if (productContent.variantConfig.variants) {
        productContent.variantConfig.variants.forEach((variant) => {
          product.setVariant(this.createProduct(variant, options));
        });
      }
      if (productContent.variantConfig.swatchColor) {
        product.shade = productContent.variantConfig.swatchColor;
      }
      if (productContent.variantConfig.baseSku) {
        product.baseSku = productContent.variantConfig.baseSku;
      }
    }

    // set options
    if (options.productStatus) {
      product.setMarketAttributes(options.productStatus);
      product.addPricingFromStatus(options.productStatus);
    }

    if (options.priceType) {
      product.setPriceAndPvFromType(options.priceType);
    }

    return product;
  }
};

function getLanguageData(languageDataArray, language) {
  if (!Array.isArray(languageDataArray) || !languageDataArray.length) {
    return null;
  }

  if (languageDataArray.length === 1) {
    return languageDataArray[0];
  }

  const fallbackLanguageData = languageDataArray.find(md => md.languageCode === "en");

  if (language) {
    const nativeLanguageData = languageDataArray.find(md => md.languageCode === language);
    return nativeLanguageData || fallbackLanguageData;
  }

  return fallbackLanguageData;
}

function getMarketData(marketDataArray, country) {
  if (!Array.isArray(marketDataArray) || !marketDataArray.length) {
    return null;
  }

  if (marketDataArray.length === 1) {
    return marketDataArray[0];
  }

  const fallbackMarketData = marketDataArray.find(md => md.countryCode === "US");

  if (country) {
    const nativeMarketData = marketDataArray.find(md => md.countryCode === country);
    return nativeMarketData || fallbackMarketData;
  }

  return fallbackMarketData;
}

module.exports = ProductContentMapper;
